import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon, 
  PrinterIcon
} from '@heroicons/react/24/outline';
import { removeCodeBlockWrapper, detectRTL } from '../../utils/downloadUtils';

interface DocumentPreviewPageProps {
  document: {
    content: string;
    originalName: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

const DocumentPreviewPage: React.FC<DocumentPreviewPageProps> = ({
  document,
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  const cleanContent = removeCodeBlockWrapper(document.content || '');
  const isRTL = detectRTL(cleanContent);

  const handlePrintAndDownload = () => {
   

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const printContent = `
          <!DOCTYPE html>
          <html lang="${isRTL ? 'ar' : 'en'}" dir="${isRTL ? 'rtl' : 'ltr'}">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${document.originalName}</title>
            <style>
              body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
              line-height: 1.6;
              margin: 20px;
              color: #333;
              direction: ${isRTL ? 'rtl' : 'ltr'};
              text-align: ${isRTL ? 'right' : 'left'};
            }
            
            h1, h2, h3, h4, h5, h6 {
              color: #2563eb;
              margin-top: 2em;
              margin-bottom: 1em;
            }
            
            p {
              margin-bottom: 1em;
            }
            
            ul, ol {
              margin-bottom: 1em;
              padding-${isRTL ? 'right' : 'left'}: 1.5em;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 1em;
            }
            
            table, th, td {
              border: 1px solid #ddd;
            }
            
            th, td {
              padding: 8px;
              text-align: ${isRTL ? 'right' : 'left'};
            }
            
            th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            
            @media print {
              body { 
                margin: 0; 
                padding: 15px; 
              }
              
              @page {
                margin: 1in;
              }
            }
          </style>
        </head>
        <body>
          ${cleanContent}
        </body>
        </html>
      `;
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-200 bg-white shadow-sm">
        <div className="flex items-center flex-1 min-w-0 mr-2 sm:mr-4">
          <h1 className="text-base sm:text-2xl font-semibold text-gray-900 truncate">
            {document.originalName}
          </h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          <button
            onClick={handlePrintAndDownload}
            className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 min-h-[44px] min-w-[44px]"
            aria-label={t('common.printDownload', 'Print/Download')}
          >
            <PrinterIcon className="h-5 w-5 sm:mr-2" />
            <span className="hidden sm:inline">{t('common.printDownload', 'Print/Download')}</span>
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md min-h-[44px] min-w-[44px]"
            aria-label={t('common.close', 'Close')}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-gray-50" style={{ height: 'calc(100vh - 60px)' }}>
        <div className="p-2 sm:p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            {/* Document preview container */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200">
              <div className="p-3 sm:p-6 md:p-8">
                <iframe
                  title={t('document.previewFrameTitle', 'Document Preview')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    width: '100%',
                    minHeight: '60vh',
                    height: 'calc(100vh - 150px)',
                    maxHeight: '80vh',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: '0.5rem',
                  }}
                  ref={el => {
                    if (el && cleanContent) {
                      // Write the content into the iframe, inheriting parent styles
                      const doc = el.contentDocument || el.contentWindow?.document;
                      if (doc && doc.body) {
                        // Clear previous content
                        doc.open();
                        doc.write(`
                          <html lang="${isRTL ? 'he' : 'en'}" dir="${isRTL ? 'rtl' : 'ltr'}">
                            <head>
                              <base target="_parent" />
                              <style>
                                body {
                                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
                                  line-height: 1.6;
                                  color: #333;
                                  background: #fff;
                                  margin: 0;
                                  padding: 0;
                                  direction: ${isRTL ? 'rtl' : 'ltr'};
                                  text-align: ${isRTL ? 'right' : 'left'};
                                }
                                .prose {
                                  max-width: none;
                                }
                              </style>
                            </head>
                            <body>
                              <div class="prose prose-lg" style="padding: 2rem;">
                                ${cleanContent}
                              </div>
                            </body>
                          </html>
                        `);
                        doc.close();
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default DocumentPreviewPage;